export const WA_RAW = "6281615674745";
export const WA_DISPLAY = "+62 816 1567 4745";
export const EMAIL = "team@vanillaglobaltransindo.com";

export const waLink = (
  text = "Hello VGT team, I'd like to discuss a vanilla beans requirement for my business."
) => `https://wa.me/${WA_RAW}?text=${encodeURIComponent(text)}`;

export const scrollToId = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const LEAD_LABEL = process.env.REACT_APP_LEAD_CONVERSION_LABEL || "";
const WA_LABEL = process.env.REACT_APP_WHATSAPP_CONVERSION_LABEL || "";

let gtagLoaded = false;
export function initAds() {
  if (!ADS_ID || gtagLoaded) return;
  gtagLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ADS_ID)}`;
  document.head.appendChild(s);
  window.gtag("js", new Date());
  window.gtag("config", ADS_ID);
}

// Pre-wired GTM / Google Ads hook. Events: generate_lead, whatsapp_click, select_product.
export const trackEvent = (event, params = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
  if (typeof window.gtag !== "function") return;
  if (event === "generate_lead" && LEAD_LABEL) {
    window.gtag("event", "conversion", { send_to: `${ADS_ID}/${LEAD_LABEL}` });
  }
  if (event === "whatsapp_click" && WA_LABEL) {
    window.gtag("event", "conversion", { send_to: `${ADS_ID}/${WA_LABEL}` });
  }
};

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again or reach us on WhatsApp.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const BUYER_TYPES = [
  "Food / Ice Cream Manufacturer",
  "Bakery / Pastry Chain",
  "Gelato / Artisan Producer",
  "Flavor / Extract Processor",
  "Importer / Spice Distributor",
  "Other B2B Buyer",
];

export const VANILLA_TYPES = [
  "Planifolia Grade A (Gourmet)",
  "Tahitensis Grade A",
  "Grade C / Extraction Grade",
  "Pure Vanilla Powder",
  "Multiple / Custom Mix",
];

export const PRODUCTS = [
  {
    id: "planifolia",
    name: "Planifolia Grade A",
    tag: "Gourmet / Premium",
    formValue: "Planifolia Grade A (Gourmet)",
    specs: [
      ["Vanillin", "2.0% – 2.4%"],
      ["Moisture", "30% – 35%"],
      ["Length", "17 – 22 cm"],
      ["Appearance", "Dark brown, oily sheen"],
    ],
    bestFor: "Gourmet gelato, artisanal chocolate, fine bakery & pastry",
    image: "/images/planifolia-grade-a.jpg",
    imageClass: "object-bottom",
  },
  {
    id: "tahitensis",
    name: "Tahitensis Grade A",
    tag: "Floral & Fruity",
    formValue: "Tahitensis Grade A",
    specs: [
      ["Vanillin", "1.8% – 2.2%"],
      ["Moisture", "30% – 35%"],
      ["Length", "15 – 20 cm"],
      ["Appearance", "Deep mahogany brown"],
    ],
    bestFor: "Craft beverages, perfumery, gourmet desserts & extracts",
    image: "/images/tahitensis-grade-a.jpg",
    imageClass: "object-bottom",
  },
  {
    id: "grade-c",
    name: "Grade C / Extraction",
    tag: "Industrial",
    formValue: "Grade C / Extraction Grade",
    specs: [
      ["Vanillin", "1.5% – 1.8%"],
      ["Moisture", "20% – 25%"],
      ["Length", "12 – 16 cm (short/split)"],
      ["Appearance", "Dark brown to black, dry"],
    ],
    bestFor: "Commercial extracts, oleoresin production, flavor manufacturers",
    image: "/images/grade-c-extraction.jpg",
    imageClass: "object-bottom",
  },
  {
    id: "powder",
    name: "Pure Vanilla Powder",
    tag: "100% Ground Beans",
    formValue: "Pure Vanilla Powder",
    specs: [
      ["Purity", "100% ground beans, 0% additives"],
      ["Mesh size", "60 – 80 mesh"],
      ["Moisture", "< 10%"],
      ["Appearance", "Rich dark brown"],
    ],
    bestFor: "Dry bakery mixes, protein powders, specialty beverage mixes",
    image: "/images/vanilla-powder.jpg",
  },
];

export const PRODUCT_SLUGS = {
  planifolia: "planifolia-grade-a",
  tahitensis: "tahitensis-grade-a",
  "grade-c": "grade-c-extraction",
  powder: "pure-vanilla-powder",
};

export const PRODUCT_PAGES = {
  "planifolia-grade-a": {
    productId: "planifolia",
    category: "Vanilla Beans",
    seoTitle: "Planifolia Grade A Vanilla Beans (Gourmet) | VGT Indonesia",
    seoDescription:
      "Indonesian Planifolia Grade A gourmet vanilla beans: 2.0-2.4% vanillin, 30-35% moisture, 17-22 cm pods. Direct export, MOQ from 1 kg — request a quotation.",
    h1: "Planifolia Grade A Vanilla Beans (Gourmet)",
    tagline: "Our flagship gourmet line — dark brown, oily and supple pods, hand-sorted for premium culinary work.",
    intro: [
      "Our Planifolia Grade A vanilla beans are cured and hand-sorted in Indonesia to gourmet standard: supple, aromatic pods measuring 17-22 cm with 30-35% moisture and 2.0-2.4% vanillin content. The dark brown, oily sheen signals a bean that splits easily and releases its seeds effortlessly.",
      "Because we source directly from curing farmers across Indonesia's vanilla-growing regions, every batch is traceable and moisture-checked before packing. Specifications are shared per batch before you commit, so the beans you approve are the beans you receive.",
    ],
    applications: [
      "Gourmet gelato & ice cream",
      "Artisanal chocolate & pralines",
      "Fine bakery & pastry",
      "Premium desserts & patisserie",
    ],
  },
  "tahitensis-grade-a": {
    productId: "tahitensis",
    category: "Vanilla Beans",
    seoTitle: "Tahitensis Grade A Vanilla Beans | VGT Indonesia",
    seoDescription:
      "Indonesian Tahitensis Grade A vanilla beans: 1.8-2.2% vanillin, 30-35% moisture, 15-20 cm pods with floral, fruity character. MOQ from 1 kg — request a quote.",
    h1: "Tahitensis Grade A Vanilla Beans",
    tagline: "The floral, fruity vanilla — deep mahogany pods prized by craft beverage makers and perfumers.",
    intro: [
      "Tahitensis Grade A offers a distinctly floral and fruity aromatic profile that sets it apart from Planifolia. Expect deep mahogany brown pods of 15-20 cm with 30-35% moisture and 1.8-2.2% vanillin content.",
      "Grown and cured in Indonesia, each batch is hand-sorted and moisture-checked before packing, with specifications shared transparently before you commit. Samples are available on request for evaluation.",
    ],
    applications: [
      "Craft beverages & syrups",
      "Perfumery & aromatics",
      "Gourmet desserts",
      "Vanilla extracts",
    ],
  },
  "grade-c-extraction": {
    productId: "grade-c",
    category: "Vanilla Beans",
    seoTitle: "Grade C Extraction Vanilla Beans | VGT Indonesia",
    seoDescription:
      "Extraction-grade Indonesian vanilla beans (Grade C): 1.5-1.8% vanillin, 20-25% moisture, 12-16 cm. Ideal for commercial extract and oleoresin production. MOQ from 1 kg.",
    h1: "Grade C Vanilla Beans (Extraction Grade)",
    tagline: "The industrial workhorse — dry, concentrated beans graded for extraction efficiency.",
    intro: [
      "Grade C vanilla beans are shorter and drier than gourmet grades: 12-16 cm pods at 20-25% moisture with 1.5-1.8% vanillin, dark brown to black in appearance. This is exactly the profile extraction facilities need for maximum yield per kilogram.",
      "Sourced directly from curing farmers across Indonesia and sorted per batch, our extraction grade is supplied to commercial extract producers and flavor houses worldwide, with batch specifications shared before you commit.",
    ],
    applications: [
      "Commercial vanilla extract",
      "Oleoresin production",
      "Flavor manufacturers",
      "Industrial food processing",
    ],
  },
  "pure-vanilla-powder": {
    productId: "powder",
    category: "Vanilla Powder",
    seoTitle: "Pure Vanilla Powder (100% Ground Beans) | VGT Indonesia",
    seoDescription:
      "100% pure Indonesian vanilla powder — ground vanilla beans only, 0% additives, 60-80 mesh, under 10% moisture. For bakery, protein and beverage mixes. MOQ from 1 kg.",
    h1: "Pure Vanilla Powder",
    tagline: "Nothing but the bean — 100% ground vanilla, zero additives, ready for dry formulations.",
    intro: [
      "Our pure vanilla powder is exactly one ingredient: Indonesian vanilla beans, milled to a free-flowing 60-80 mesh with moisture below 10%. No carriers, no sugar, no additives — just full-strength vanilla that disperses evenly through dry formulations.",
      "Every batch is moisture-checked before packing and double vacuum-sealed in food-grade foil pouches, so the powder arrives with its aroma intact. Batch specifications are shared before you commit.",
    ],
    applications: [
      "Dry bakery & cake mixes",
      "Protein & nutrition powders",
      "Specialty beverage mixes",
      "Seasoning & flavor blends",
    ],
  },
};

export const BENEFITS = [
  {
    icon: "Sprout",
    title: "Direct Plantation Sourcing",
    desc: "Sourced directly from curing farmers across Indonesia's vanilla-growing regions, with batch-level origin traceability.",
  },
  {
    icon: "Scale",
    title: "Flexible MOQ from 1 kg",
    desc: "From 1 kg evaluation orders to full commercial volumes — built for both craft producers and industrial buyers.",
  },
  {
    icon: "BadgeCheck",
    title: "Batch-by-Batch Quality Control",
    desc: "Hand-sorted and moisture-checked per batch, with specifications shared transparently before you commit.",
  },
  {
    icon: "Package",
    title: "Export-Grade Vacuum Packaging",
    desc: "Double vacuum-sealed in food-grade foil pouches to lock in aroma and protect moisture through transit.",
  },
  {
    icon: "Plane",
    title: "Worldwide Shipping Support",
    desc: "We arrange express air freight and assist with export documentation for destinations across five continents.",
  },
  {
    icon: "Timer",
    title: "Fast Quotation Response",
    desc: "A dedicated export team answers quotation requests quickly on WhatsApp and email during business hours.",
  },
];

export const STEPS = [
  {
    step: "01",
    title: "Send Your Requirement",
    desc: "Submit the quotation form or message us on WhatsApp with your product, grade, and estimated quantity.",
  },
  {
    step: "02",
    title: "Receive a Custom Quotation",
    desc: "We reply with product pricing and destination-specific shipping options for your approval.",
  },
  {
    step: "03",
    title: "Verify Quality with Samples",
    desc: "Request a sample pack to check moisture, aroma, and vanillin before confirming a commercial order.",
  },
  {
    step: "04",
    title: "Packed, Documented & Shipped",
    desc: "Approved orders are vacuum-packed, issued export documentation, and dispatched via air freight.",
  },
];

export const FAQS = [
  {
    q: "What is Vanilla Global Transindo's minimum order quantity (MOQ)?",
    a: "Our MOQ starts from just 1 kg. We support small evaluation orders for growing producers as well as full commercial volumes for industrial buyers.",
  },
  {
    q: "Do you provide samples before a commercial order?",
    a: "Yes — samples are available on request. Sample and shipping costs depend on the product and destination, and are quoted per inquiry.",
  },
  {
    q: "What export documentation do you provide?",
    a: "Where applicable, shipments include a Phytosanitary Certificate, Certificate of Analysis (COA), Commercial Invoice, and Packing List. We assist with the export paperwork for your destination.",
  },
  {
    q: "How long does shipping take from Indonesia?",
    a: "Express air freight typically reaches North America, Europe, the Middle East, Asia, and Australia within 4–8 business days after dispatch.",
  },
  {
    q: "What are the specs of your Planifolia Grade A beans?",
    a: "Planifolia Grade A carries 30–35% moisture and 2.0–2.4% vanillin, with beans measuring 17–22 cm — supple, aromatic, and easy to split.",
  },
  {
    q: "How are the vanilla beans packaged for export?",
    a: "Our vanilla beans are double vacuum-packed in food-grade foil pouches and secured in rigid master cartons to preserve aroma and moisture during transit.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept international bank transfer (T/T). Payment terms for your order are confirmed together with your quotation.",
  },
];
